import { Fragment, useEffect, useRef, useState } from "react";
import { Menu, Add, Check, Logout, Upgrade } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu as MuiMenu,
  MenuItem,
  Toolbar,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import { useAccountContext } from "@root/stores/accountStore";
import { useEventPageContext } from "@root/stores/eventPageStore";
import { observer } from "mobx-react-lite";
import { UserAvatar } from "../common/UserAvatar";
import { Logo } from "../common/Logo";
import { navigate } from "vike/client/router";
import { accountService } from "@root/services/api/accountService";
import { sidebarStore } from "@root/stores/sidebarStore";
import { NotificationBell } from "../notifications/NotificationBell";
import { NotificationDropdown } from "../notifications/NotificationDropdown";
import { useNotificationContext } from "@root/contexts/NotificationContext";

interface LoggedInNavigationProps {
  showHamburger?: boolean;
}

export const LoggedInNavigation = observer(
  ({ showHamburger = true }: LoggedInNavigationProps = {}) => {
    const accountStore = useAccountContext();
    const eventPageStore = useEventPageContext();
    const { unreadCount } = useNotificationContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [notificationAnchorEl, setNotificationAnchorEl] =
      useState<null | HTMLElement>(null);
    const profileNameRefs = useRef<Record<string, HTMLElement | null>>({});
    const [overflowedProfiles, setOverflowedProfiles] = useState<
      Record<string, boolean>
    >({});

    useEffect(() => {
      if (!anchorEl) return;

      const updateOverflowedProfiles = () => {
        const next: Record<string, boolean> = {};

        accountStore.profiles.forEach((profile) => {
          const profileId = String(profile.id);
          const el = profileNameRefs.current[profileId];
          next[profileId] = !!(
            el &&
            (el.scrollHeight > el.clientHeight + 1 ||
              el.scrollWidth > el.clientWidth + 1)
          );
        });

        setOverflowedProfiles((prev) => {
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(next);

          if (prevKeys.length !== nextKeys.length) return next;
          for (const key of nextKeys) {
            if (prev[key] !== next[key]) return next;
          }
          return prev;
        });
      };

      const raf = requestAnimationFrame(updateOverflowedProfiles);
      window.addEventListener("resize", updateOverflowedProfiles);

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", updateOverflowedProfiles);
      };
    }, [
      anchorEl,
      accountStore.profiles
        .map((profile) => `${profile.id}:${profile.name ?? ""}`)
        .join("|"),
    ]);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
      setAnchorEl(null);
    };

    const handleNotificationMenuOpen = (
      event: React.MouseEvent<HTMLElement>
    ) => {
      setNotificationAnchorEl(event.currentTarget);
    };

    const handleNotificationMenuClose = () => {
      setNotificationAnchorEl(null);
    };

    const handleSignOut = async () => {
      try {
        console.log("Sign out clicked"); // Debug log
        handleProfileMenuClose();

        // Call logout API
        await accountService.logout();

        // Clear session from localStorage
        accountStore.clearAccountSession();

        // Redirect to home page (this will reload and clear the account store)
        window.location.href = "/";
      } catch (error) {
        console.error("Logout error:", error);
        // Even if API fails, clear local session and redirect
        accountStore.clearAccountSession();
        window.location.href = "/";
      }
    };

    const handleSwitchProfile = async (session: any) => {
      handleProfileMenuClose();
      // Clear any open organization/event views before switching
      eventPageStore.hideOrganization();
      eventPageStore.hideEvent();
      await accountStore.switchProfile(session);

      // Use client-side navigation instead of full page reload for faster switching
      navigate("/");
    };

    const handleCreateOrganization = async () => {
      navigate("/account/organizations/create");
    };

    const handlePlanClick = () => {
      handleProfileMenuClose();
      navigate("/subscription");
    };

    return (
      <Fragment>
        <AppBar sx={{ padding: 0, zIndex: 1500 }}>
          <Toolbar
            variant="dense"
            sx={{
              minHeight: {
                xs: "60px !important",
                sm: "64px !important",
                md: "68px !important",
              },
              height: { xs: "60px", sm: "64px", md: "68px" },
              padding: "0 !important",
            }}
          >
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: "3rem",
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  px: "1rem",
                },
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: "0.75rem", sm: "1.5rem" },
                }}
              >
                {showHamburger && (
                  <IconButton
                    size="medium"
                    onClick={() => sidebarStore.toggle()}
                    sx={{ padding: 0 }}
                  >
                    <Menu
                      sx={(theme) => ({
                        color: theme.palette.primary.main,
                        fontSize: "1.5rem",
                      })}
                    />
                  </IconButton>
                )}
                <Logo />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: "0.5rem", sm: "1rem" },
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateOrganization}
                  color="primary"
                  sx={(theme) => ({
                    textTransform: "none",
                    borderRadius: "24px",
                    padding: "0.625rem 1.5rem",
                    fontWeight: 600,
                    fontSize: "1rem",
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "auto",
                      padding: "0.5rem",
                      fontSize: "0.875rem",
                      "& .MuiButton-startIcon": {
                        margin: 0,
                      },
                      "& .MuiButton-text": {
                        display: "none",
                      },
                    },
                  })}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    Create Organization
                  </Box>
                </Button>
                <NotificationBell onClick={handleNotificationMenuOpen} unreadCount={unreadCount} />
                <IconButton
                  size="medium"
                  aria-label="account of current user"
                  aria-controls="user-menu"
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                  sx={{
                    padding: 0,
                    backgroundColor: "transparent !important",
                  }}
                >
                  {accountStore.account && accountStore.session && (
                    <UserAvatar
                      useStore={true}
                      key={`avatar-${accountStore.session.id}-${accountStore.sessionVersion}`}
                    />
                  )}
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <Toolbar
          variant="dense"
          sx={{
            minHeight: {
              xs: "60px !important",
              sm: "64px !important",
              md: "68px !important",
            },
            height: { xs: "60px", sm: "64px", md: "68px" },
          }}
        />
        <MuiMenu
          id="user-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            mt: 1,
          }}
          PaperProps={{
            sx: {
              width: "350px",
              maxWidth: "350px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
          }}
        >
          {/* Account Header */}
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
              Choose an Account
            </Typography>
          </Box>
          <Divider sx={{ mb: 0.5 }} />
          <MenuItem
            onClick={handlePlanClick}
            sx={{
              py: 1,
              px: 2,
              backgroundColor: "rgba(239, 68, 68, 0.08)",
              borderRadius: "6px",
              margin: "4px 8px",
              "&:hover": {
                backgroundColor: "rgba(239, 68, 68, 0.12)",
              },
            }}
          >
            <Upgrade
              sx={{
                color: "#EF4444",
                fontSize: "1.25rem",
                mr: 1.5,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#EF4444",
              }}
            >
              {accountStore.isOrganizationProfile
                ? "My Organization Plan"
                : "My Personal Plan"}
            </Typography>
          </MenuItem>
          <Divider sx={{ mt: 0.5, mb: 1 }} />


          {/* Account List */}
          {accountStore.profiles.map((profile) => {
            const isCurrentAccount =
              accountStore.session?.id === profile.id;
            const planName = profile.planName || "Free";
            const planColor = profile.planColor || "#CC0000";

            // Calculate initials from profile name
            const getInitials = (name: string) => {
              return name
                ?.split(" ")
                .map((s) => s.substring(0, 1))
                .slice(0, 2)
                .join("")
                .toUpperCase();
            };

            return (
              <Tooltip
                key={profile.id}
                title={profile.name || ""}
                arrow
                placement="top"
                disableHoverListener={
                  !overflowedProfiles[String(profile.id)] || !profile.name
                }
                disableFocusListener={
                  !overflowedProfiles[String(profile.id)] || !profile.name
                }
                disableTouchListener={
                  !overflowedProfiles[String(profile.id)] || !profile.name
                }
              >
                <MenuItem
                  onClick={() => handleSwitchProfile(profile)}
                  selected={isCurrentAccount}
                  sx={{
                    py: 1,
                    px: 2,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                    backgroundColor: isCurrentAccount
                      ? "rgba(0, 0, 0, 0.04)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    },
                  }}
                >
                  <UserAvatar
                    fallbackText={getInitials(profile.name)}
                    imageUrl={profile.imageUrl || (profile.type === "user" ? accountStore.account?.imageUrl : undefined)}
                    useStore={false}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      ref={(el) => {
                        profileNameRefs.current[String(profile.id)] = el;
                      }}
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        lineHeight: 1.2,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                      }}
                    >
                      {profile.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mt: 0.5,
                      }}
                    >
                      {profile.type === "user"
                        ? accountStore.account?.email
                        : "Organization"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        borderRadius: "4px",
                        background: `linear-gradient(135deg, ${planColor} 0%, ${planColor}DD 100%)`,
                        color: "#FFFFFF",
                        padding: "2px 10px",
                        minWidth: "50px",
                        textAlign: "center",
                      }}
                    >
                      {planName}
                    </Box>

                    <Box
                      sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: isCurrentAccount ? "transparent" : "#E5E7EB",
                        backgroundColor: isCurrentAccount
                          ? planColor
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {isCurrentAccount && (
                        <Check
                          sx={{
                            fontSize: "16px",
                            color: "#FFFFFF",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              </Tooltip>
            );
          })}


          <Divider sx={{ my: 0.5 }} />

          <MenuItem
            onClick={handleSignOut}
            sx={{
              py: 1,
              px: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "#F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Logout sx={{ color: "#374151" }} />
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
              Log Out
            </Typography>
          </MenuItem>


        </MuiMenu>

        {/* Notification Dropdown */}
        <NotificationDropdown
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
        />
      </Fragment>
    );
  }
);
